import { useSelector } from 'react-redux';
import { selectCurrentUser, selectUserRoles } from '../../features/auth/authSlice';
import { useRoleBasedFiltering } from '../../utils/roleBasedFiltering';
import { formatCurrency } from '../../utils/currencyUtils';
import OmCard from '../ui/OmCard';

const FinancialDetails = ({ claim }) => {
  const user = useSelector(selectCurrentUser);
  const userRoles = useSelector(selectUserRoles);
  const { filtering } = useRoleBasedFiltering(userRoles, user);
  
  const shouldShowBenefitAmount = filtering.canViewBenefitAmounts(claim);

  return (
    <OmCard className="bg-crystal-white shadow-soft border border-steel/20 rounded-lg overflow-hidden transition-all duration-300 ease-in-out hover:shadow-lg hover:-translate-y-1 hover:border-fresh-leaf/30 focus-within:ring-2 focus-within:ring-fresh-leaf/25 focus-within:border-fresh-leaf/40">
      <div className="px-6 sm:px-8 pt-6 sm:pt-8 pb-4 sm:pb-6 border-b border-steel/15">
        <h3 className="text-xl sm:text-2xl font-semibold text-graphite mb-3">Financial Details</h3>
        <p className="text-sm sm:text-base text-steel">Benefit amount and banking information</p>
      </div>
      <div className="p-6 sm:p-8">
        {/* Benefit Amount Section - Prominent display */}
        <div className="mb-8 p-4 bg-gradient-to-r from-fresh-leaf/5 to-forest-fern/5 border border-fresh-leaf/20 rounded-lg">
          <div className="space-y-3 group cursor-default">
            <label className="block text-sm font-semibold text-woodland-moss uppercase tracking-wider flex items-center">
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                      d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
              </svg>
              Benefit Amount
            </label>
            <div className="flex items-center space-x-3">
              <p className="text-2xl sm:text-3xl text-woodland-moss font-bold font-mono transition-all duration-200 group-hover:text-forest-fern group-hover:scale-[1.02] select-all">
                {shouldShowBenefitAmount ? formatCurrency(claim.approvedBenefitAmount || claim.benefitAmount || 0) : '***'}
              </p>
              {!shouldShowBenefitAmount && (
                <span className="text-xs bg-warning/10 text-warning px-2 py-1 rounded-full font-medium">
                  Pending Approval
                </span>
              )}
              {claim.benefitApprovedAt && (
                <span className="text-xs bg-success/10 text-success px-2 py-1 rounded-full font-medium">
                  Approved
                </span>
              )}
            </div>
            {claim.benefitApprovedAt && (
              <p className="text-xs text-steel">
                Approved on {new Date(claim.benefitApprovedAt).toLocaleDateString()}
                {claim.benefitApprovedBy && ` by ${claim.benefitApprovedBy.firstName} ${claim.benefitApprovedBy.lastName}`}
              </p>
            )}
          </div>
        </div>

        {/* Banking Information */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 sm:gap-8">
          <div className="space-y-3 group cursor-default">
            <label className="block text-xs sm:text-sm font-medium text-steel uppercase tracking-wider transition-colors duration-200 group-hover:text-forest-fern">Bank Account Name</label>
            <p className="text-sm sm:text-base text-graphite font-medium transition-all duration-200 group-hover:text-deep-jungle group-hover:scale-[1.02] select-all">{claim.bankAccountName || 'N/A'}</p>
          </div>
          <div className="space-y-3 group cursor-default">
            <label className="block text-xs sm:text-sm font-medium text-steel uppercase tracking-wider transition-colors duration-200 group-hover:text-forest-fern">Bank Name</label>
            <p className="text-sm sm:text-base text-graphite font-medium transition-all duration-200 group-hover:text-deep-jungle group-hover:scale-[1.02] select-all">{claim.bankName || 'N/A'}</p>
          </div>
          <div className="space-y-3 group cursor-default">
            <label className="block text-xs sm:text-sm font-medium text-steel uppercase tracking-wider transition-colors duration-200 group-hover:text-forest-fern">Account Number</label>
            <p className="text-sm sm:text-base text-graphite font-medium font-mono transition-all duration-200 group-hover:text-deep-jungle group-hover:scale-[1.02] select-all">{claim.accountNumber || 'N/A'}</p>
          </div>
          <div className="space-y-3 group cursor-default">
            <label className="block text-xs sm:text-sm font-medium text-steel uppercase tracking-wider transition-colors duration-200 group-hover:text-forest-fern">Branch Code</label>
            <p className="text-sm sm:text-base text-graphite font-medium font-mono transition-all duration-200 group-hover:text-deep-jungle group-hover:scale-[1.02] select-all">{claim.branch || 'N/A'}</p>
          </div>
        </div>
      </div>
    </OmCard>
  );
};

export default FinancialDetails;
